import { type FC, useEffect, useRef, useState } from "react";
import { Form } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "../common/form/useForm";
import { useModal } from "../common/modal/Modal";
import { ExtraForm } from "../common/pure/ExtraFormGroup";
import { Button } from "../components/Button";
import { StatusPlaceholder } from "../components/StatusPlaceholder";
import { Tabs } from "../components/Tabs";
import { LinkGen } from "../LinkGen";
import { AppPage } from "../layout/AppLayout";
import {
	useEnsureMasterLocation,
	useGoogleMyBusiness,
	useGoogleMyBusinessActions,
	useGoogleMyBusinessLocation,
	useGoogleMyBusinessLocationAttributes,
	useGoogleMyBusinessLocations,
	useGoogleMyBusinessSelectedLocation,
	useMasterLocation,
} from "../settings/useIntegrations";
import { Svg } from "../svg";
import { BusinessReviews } from "./BusinessReviews";
import {
	FormBusinessLocationFields,
	type FormMyBusinessLocationFields,
} from "./FormBusinessLocationFields";
import {
	buildLocationUpdateMask,
	formLocationValuesToMasterUpdateDto,
	formValuesFromMaster,
	locationToFormValues,
} from "./helpers";
import { ModalReviewReplyRules } from "./ModalReviewReplyRules";
import styles from "./PageGoogleMyBusiness.module.css";
import { PlatformStatusBar } from "./PlatformStatusBar";
import { ProfileHistory } from "./ProfileHistory";

const NotConnected: FC = () => {
	const navigate = useNavigate();
	return (
		<StatusPlaceholder
			label="Not connected"
			sublabel="Go to your applications and connect your Google My Business profile"
			image={Svg.Monitor}
		>
			<Button
				icon="arrow_forward"
				size="sm"
				onClick={() => navigate(LinkGen.Settings.Apps())}
			>
				Applications
			</Button>
		</StatusPlaceholder>
	);
};

const NoLocations: FC = () => {
	const navigate = useNavigate();
	return (
		<StatusPlaceholder
			label="No business locations found"
			sublabel="Go to applications and make sure you have connected the correct account and selected the correct group"
			image={Svg.Monitor}
		>
			<Button
				icon="arrow_forward"
				size="sm"
				onClick={() => navigate(LinkGen.Settings.Apps())}
			>
				Applications
			</Button>
		</StatusPlaceholder>
	);
};

const PageConnected: FC = () => {
	const locations = useGoogleMyBusinessLocations();
	const selectedLocation = useGoogleMyBusinessSelectedLocation();
	const { setSelectedLocation } = useGoogleMyBusinessActions();
	// Default to the GBP selected for this account; fall back to the first
	// location only when none has been selected yet (or it no longer exists
	// under the current account).
	const [locationID, setLocationID] = useState(() => {
		const stored = locations.find(({ id }) => id === selectedLocation?.id);
		return stored?.id ?? locations[0]?.id;
	});
	// Each Google location has its own master profile.
	const masterSeed = useEnsureMasterLocation(locationID);
	const { tab = "profile" } = useParams();
	const navigate = useNavigate();
	const { open } = useModal();

	// No location selected for this account yet (or the stored one no longer
	// exists under it) — persist the fallback so the selection becomes the
	// account's canonical GBP.
	const defaultPersisted = useRef(false);
	const storedIsValid = locations.some(({ id }) => id === selectedLocation?.id);
	useEffect(() => {
		if (defaultPersisted.current || storedIsValid || !locationID) {
			return;
		}
		defaultPersisted.current = true;
		setSelectedLocation.mutate({ id: locationID });
	}, [storedIsValid, locationID, setSelectedLocation.mutate]);

	if (locations.length === 0) {
		return (
			<AppPage title="Business Manager">
				<NoLocations />
			</AppPage>
		);
	}

	return (
		<AppPage
			title="Google My Business"
			headerActions={
				<ExtraForm.Select
					value={locationID}
					onChange={(e) => {
						setLocationID(e.target.value);
						// Persist so the whole product (posting, ads targeting) reads
						// from and writes to this profile.
						setSelectedLocation.mutate({ id: e.target.value });
					}}
					id="google-my-business-locations"
				>
					{locations.map(({ id, title }) => (
						<option key={id} value={id}>
							{title}
						</option>
					))}
				</ExtraForm.Select>
			}
		>
			<PlatformStatusBar locationId={locationID} />
			<ProfileHistory locationId={locationID} />
			<Tabs
				// change key to handle url changes within the page
				// this way if the tab param changes on the page it will update the active tab
				key={`mybusiness-tab-${tab}`}
				onTabChange={(tab) => {
					navigate(LinkGen.Google.MyBusiness.Tab(tab || "apps"));
				}}
				defaultActiveKey={tab}
				actions={
					<Button
						variant="ghost"
						size="sm"
						icon="rule"
						onClick={() => open(ModalReviewReplyRules, {})}
					>
						Rules
					</Button>
				}
			>
				<Tabs.Panel eventKey="profile" title="Business Profile">
					{/* Remount per location so the baseline used for the change-diff
					    always matches the selected location. Wait until the master
					    query settles so the form opens on the saved profile. */}
					{locationID && masterSeed.isFetched ? (
						<PageBusinessForm key={locationID} locationID={locationID} />
					) : null}
				</Tabs.Panel>
				<Tabs.Panel eventKey="reviews" title="Reviews">
					{locationID ? (
						<BusinessReviews key={locationID} locationId={locationID} />
					) : null}
				</Tabs.Panel>
			</Tabs>
		</AppPage>
	);
};

function PageBusinessForm({ locationID }: { locationID: string }) {
	const { updateMasterProfile, refreshLocation } = useGoogleMyBusinessActions();
	const location = useGoogleMyBusinessLocation(locationID);
	const attributes = useGoogleMyBusinessLocationAttributes(locationID);
	const master = useMasterLocation(locationID);

	// Freeze the initial form values once so the change-diff at submit uses the
	// values the fields were first populated with, not a later background
	// refetch. The component is keyed on locationID, so switching locations
	// remounts and loads that location's own master.
	const baselineRef = useRef<FormMyBusinessLocationFields | null>(null);
	const masterForLocation =
		master.data && sameGoogleLocation(master.data.googleLocationId, locationID)
			? master.data
			: null;
	if (baselineRef.current === null && !master.isLoading) {
		const fromGoogle = locationToFormValues(location, attributes);
		baselineRef.current = masterForLocation
			? formValuesFromMaster(masterForLocation, fromGoogle)
			: { ...fromGoogle, legalName: "" };
	}
	const initialValues = baselineRef.current ?? {
		...locationToFormValues(location, attributes),
		legalName: masterForLocation?.legalName ?? "",
	};

	const form = useForm<FormMyBusinessLocationFields>({
		initialValues,
		onSubmit: async (values) => {
			const updateMask = buildLocationUpdateMask(initialValues, values);
			const dto = formLocationValuesToMasterUpdateDto(values, updateMask);

			await updateMasterProfile.mutateAsync({
				location: locationID,
				dto,
			});
		},
	});

	// Master may resolve after the form freezes — seed legal name once.
	useEffect(() => {
		if (
			masterForLocation?.legalName &&
			!form.values.legalName &&
			baselineRef.current?.legalName === ""
		) {
			form.setFieldValue("legalName", masterForLocation.legalName);
			baselineRef.current = {
				...baselineRef.current,
				legalName: masterForLocation.legalName,
			};
		}
	}, [masterForLocation?.legalName, form.values.legalName, form.setFieldValue]);

	return (
		<Form onSubmit={form.handleSubmit} className="my-business">
			<div className={styles.bar}>
				<h2>Business Profile</h2>
				<div style={{ display: "flex", gap: 8 }}>
					<Button
						variant="ghost"
						size="sm"
						icon="sync"
						loading={refreshLocation.isPending}
						onClick={() => refreshLocation.mutate(locationID)}
					>
						Refresh
					</Button>
					<Button type="submit" loading={form.isSubmitting}>
						Save Profile
					</Button>
				</div>
			</div>
			<FormBusinessLocationFields form={form} locationID={locationID} />
		</Form>
	);
}

const PageNotConnected: FC = () => {
	return (
		<AppPage title="Business Manager">
			<NotConnected />
		</AppPage>
	);
};

function sameGoogleLocation(
	stored: string | null | undefined,
	selected: string,
) {
	if (!stored) return false;
	const normalize = (id: string) => id.replace(/^locations\//, "");
	return normalize(stored) === normalize(selected);
}

export const PageGoogleMyBusiness: FC = () => {
	const { status } = useGoogleMyBusiness();

	return status.data.connected ? <PageConnected /> : <PageNotConnected />;
};
